/**
 * Storage area for the operator SPA (Inventory Storage Locations feature).
 *
 * A single page with a section switcher covering every storage workflow:
 *   - Create entities (Rack / Shelf / Box / Row / Storage_Batch) → POST /storage/{type}
 *   - Putaway driven by scanning and Pick driven by typing identifiers
 *   - A guided putaway wizard (Batch → Row) against
 *     POST /storage/scan-putaway, surfacing wrong-entity-type inline
 *   - Per-card location view → GET /storage/cards/{id}/location
 *   - Picklist upload, grouped/sortable by rack → shelf → box → row → batch
 *   - Paginated Rack / Shelf / Box / Row / Batch lists → GET /storage/{type}
 *   - Label/print view rendering the QR_Token → GET /storage/{type}/{id}/labels
 *   - Transaction-history view → GET /storage/{type}/{id}/transactions
 *
 * All non-trivial display logic (picklist grouping/sorting, placement-state
 * formatting) lives in the pure helpers in `./placement` so it is unit-testable
 * without a DOM. This file is the rendering shell over those helpers and the
 * `useStorageApi()` accessors. Reuses the existing SPA auth plumbing; no device
 * credential is involved (Requirement 14.5).
 *
 * Validates: Requirements 7.3, 9.4, 10.1, 10.2, 14.5, 19
 */

import { Fragment, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type * as React from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { ApiResult } from '../auth/api-client';
import { describePlacement, groupPicklist, type PlacementDisplay } from './placement';
import {
  appendPage,
  buildLabelSheet,
  containerListingEntryMatches,
  createdEntriesForType,
  entityDetailHref,
  formatListingDate,
  labelRequestRef,
  prependCreatedEntry,
  sortEntriesByDate,
  toggleSelection,
  type LabelResultRef,
  type LabelTile as LabelTileData,
  type SortDirection,
} from './container-listing';
import { LabelTile } from './LabelTile';
import { printLabelSheet, printSingleLabel } from './label-print';
import {
  useStorageApi,
  type ContainerType,
  type LabelType,
  type ScanResponse,
  type LabelResponse,
  type PickResponse,
  type PutawayResponse,
  putawayFailureMessage,
} from './storage-api';
import { CardLabelButton } from './CardLabelButton';
import { InventoryLocationsSection } from './InventoryLocationsSection';
import { classifyContainerPlacement, storageEntityTypeFromId } from './inventory-hierarchy';
import { CardSearchPanel } from '../catalog/CatalogListPage';
import { useCatalogApi } from '../catalog/catalog-api';
import { IntakePage } from '../intake/IntakePage';
import {
  batchBrowseReturnContext,
  BATCH_RESULT_LIMITS,
  browseReturnContextFrom,
  supportsBulkLabelPrinting,
  type BatchBrowseSnapshot,
  type BatchResultLimit,
} from './batch-browse-state';
import { positionBatchHoverPreview, type HoverPreviewPosition } from './batch-hover-preview';
import { FulfillmentPickWorkbench } from './FulfillmentPickWorkbench';
import type {
  CardIdentity,
  CardRecord,
  ContainerListingEntry,
  IdentityStatus,
  LocationResolution,
  PicklistEntry,
  StorageBatchWorkflowStatus,
} from '../shared/types';

// Lazy-loaded so the ~400KB `@zxing` QR-decoder bundle is only fetched when an
// operator actually opens the camera scanner, not on first page load.
const QrScanner = lazy(() => import('./QrScanner').then((m) => ({ default: m.QrScanner })));

// ---------------------------------------------------------------------------
// Section switcher
// ---------------------------------------------------------------------------

const SECTIONS = [
  'New Container',
  'Manual Capture',
  'Locations',
  'Racks',
  'Shelves',
  'Boxes',
  'Rows',
  'Batches',
  'Cards',
  'Putaway',
  'Pick',
  'Find',
  'Picklist',
  'Transactions',
] as const;
type Section = (typeof SECTIONS)[number];

/**
 * The storage sections clustered by operator intent rather than listed flat.
 * The sidebar renders these groups in order; the grouping is purely a
 * navigation/presentation concern — each section component is unchanged.
 *   - Find:    locating cards and containers
 *   - Browse:  container lists and labels
 *   - Work:    the daily physical work (putaway + pick)
 *   - Create:  occasional container creation + manual card capture/intake
 *   - History: the audit trail
 *
 * Browsing a container's contents is no longer a section: each entity in the
 * Shelf / Box / Batch lists links to its own detail page (`/storage/{type}/{id}`), which lists
 * its contents as drill-down links. (The old in-page drill-down panel and the
 * standalone "Contents" lookup section were removed in favor of that.)
 *
 * Label printing is NOT a group of its own: container labels live in
 * Browse › Racks / Shelves / Boxes / Rows / Batches (per-row + multi-select print), and
 * individual card labels are printed inline at Pick and in Find — the moments a card needs one.
 */
const SECTION_GROUPS: { label: string; sections: Section[] }[] = [
  { label: 'Find', sections: ['Find', 'Picklist'] },
  {
    label: 'Browse',
    sections: ['Locations', 'Racks', 'Shelves', 'Boxes', 'Rows', 'Batches', 'Cards'],
  },
  { label: 'Work', sections: ['Putaway', 'Pick'] },
  { label: 'Create', sections: ['New Container', 'Manual Capture'] },
  { label: 'History', sections: ['Transactions'] },
];

export function StoragePage(): JSX.Element {
  // Section + drill-down place are mirrored in the URL query string so that
  // navigating to a card detail page and pressing the browser Back button
  // restores the operator's exact spot (which section, and how deep into a
  // container they had drilled). Default to the most common daily action.
  const [params, setParams] = useSearchParams();
  const sectionParam = params.get('section');
  const normalizedSectionParam =
    sectionParam === 'Browse containers'
      ? 'Batches'
      : sectionParam === 'Scan-driven putaway'
        ? 'Putaway'
        : sectionParam === 'Putaway / Move / Pick'
          ? 'Pick'
          : sectionParam;
  const section: Section = SECTIONS.includes(normalizedSectionParam as Section)
    ? (normalizedSectionParam as Section)
    : 'Putaway';
  const setSection = useCallback(
    (s: Section) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('section', s);
          // Leaving a section abandons any drill-down it owned.
          next.delete('drill');
          next.delete('item');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  return (
    <div>
      <h2>Inventory</h2>
      <p className="tcvs-page-intro">
        Warehouse inventory: create containers, put inventory away, pull cards, and resolve where
        any card lives. Scan a QR label with a handheld scanner or type a token.
      </p>
      <div className="tcvs-storage-layout">
        <nav className="tcvs-storage-nav" aria-label="Inventory sections">
          {SECTION_GROUPS.map((group) => (
            <div key={group.label} className="tcvs-storage-nav-group">
              <div className="tcvs-storage-nav-heading">{group.label}</div>
              {group.sections.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSection(s)}
                  aria-current={s === section ? 'page' : undefined}
                  className={
                    s === section
                      ? 'tcvs-storage-nav-item tcvs-storage-nav-item-active'
                      : 'tcvs-storage-nav-item'
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="tcvs-storage-content">
          {section === 'New Container' ? <CreateSection /> : null}
          {section === 'Manual Capture' ? <IntakePage /> : null}
          {section === 'Locations' ? <InventoryLocationsSection /> : null}
          {section === 'Racks' ? <BrowseSection type="racks" /> : null}
          {section === 'Shelves' ? <BrowseSection type="shelves" /> : null}
          {section === 'Boxes' ? <BrowseSection type="boxes" /> : null}
          {section === 'Rows' ? <BrowseSection type="rows" /> : null}
          {section === 'Batches' ? <BrowseSection type="batches" /> : null}
          {section === 'Cards' ? (
            <CardSearchPanel
              title="Search cards"
              intro="Filter card records by category, set, or name."
              showSkuFilter={false}
            />
          ) : null}
          {section === 'Putaway' ? <ScanPutawaySection /> : null}
          {section === 'Pick' ? <PickSection /> : null}
          {section === 'Find' ? <FindSection /> : null}
          {section === 'Picklist' ? <PicklistSection /> : null}
          {section === 'Transactions' ? <TransactionsSection /> : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

const cellStyle: React.CSSProperties = {
  border: '1px solid var(--tcvs-border)',
  padding: '4px 8px',
  textAlign: 'left',
};

const toneClass: Record<PlacementDisplay['tone'], string> = {
  stored: 'tcvs-tone-stored',
  unplaced: 'tcvs-tone-unplaced',
  pulled: 'tcvs-tone-pulled',
  absent: 'tcvs-tone-absent',
  unknown: 'tcvs-tone-unknown',
};

/** Render a JSON-failure envelope as a one-line crimson message. */
function ErrorLine({ error }: { error: string | null }): JSX.Element | null {
  if (error === null) return null;
  return <p className="tcvs-error">Error: {error}</p>;
}

/** Extract a human message from an ApiResult failure. */
function failMessage(result: { ok: false; error: string; message?: string }): string {
  return result.message ?? result.error;
}

/** Render a {@link PlacementDisplay} as a compact inline descriptor. */
function PlacementBadge({ display }: { display: PlacementDisplay }): JSX.Element {
  return (
    <span>
      <strong className={toneClass[display.tone]}>{display.label}</strong>
      {display.showPath && display.path !== undefined ? (
        <span className="tcvs-mono" style={{ marginLeft: 8 }}>
          {[
            display.path.rack !== undefined ? `Rack ${display.path.rack.rackCode}` : undefined,
            `Shelf ${display.path.shelf.shelfCode}`,
            `Box ${display.path.box.entityId}`,
            display.path.row !== undefined
              ? `Row ${display.path.row.rowCode ?? display.path.row.entityId}`
              : undefined,
            `Batch ${display.path.batch.entityId}`,
          ]
            .filter((part): part is string => part !== undefined)
            .join(' -> ')}
        </span>
      ) : null}
      {display.batch !== undefined ? (
        <span className="tcvs-mono" style={{ marginLeft: 8 }}>
          Batch {display.batch.entityId}
        </span>
      ) : null}
    </span>
  );
}

function formatEntityScanPath(path: {
  rack?: { entityId: string; rackCode: string };
  shelf?: { entityId: string; shelfCode: string };
  box?: { entityId: string };
  row?: { entityId: string; rowCode?: string };
  batch?: { entityId: string };
}): string {
  const parts = [
    path.rack !== undefined ? `Rack ${path.rack.rackCode}` : undefined,
    path.shelf !== undefined ? `Shelf ${path.shelf.shelfCode}` : undefined,
    path.box !== undefined ? `Box ${path.box.entityId}` : undefined,
    path.row !== undefined ? `Row ${path.row.rowCode ?? path.row.entityId}` : undefined,
    path.batch !== undefined ? `Batch ${path.batch.entityId}` : undefined,
  ].filter((part): part is string => part !== undefined);
  return parts.length === 0 ? 'placed' : `on ${parts.join(' -> ')}`;
}

/**
 * Compact identity-status badge for the storage views. An `UNIDENTIFIED` card
 * (one created at device Upload that an operator has not yet reviewed) is shown
 * distinctly from an `IDENTIFIED` one, reusing the same design-token tone
 * classes as the catalog's badge for visual consistency. Kept local to the
 * storage view to avoid coupling to the shared catalog components.
 */
function CardIdentityBadge({ status }: { status: IdentityStatus }): JSX.Element {
  const identified = status === 'IDENTIFIED';
  return (
    <strong className={identified ? 'tcvs-tone-stored' : 'tcvs-tone-unplaced'}>
      {identified ? 'Identified' : 'Unidentified'}
    </strong>
  );
}

/** Pretty-print an authoritative {@link CardIdentity} for an inline summary. */
function formatCardIdentity(identity: CardIdentity): string {
  return `${identity.category} · ${identity.set} #${identity.number} ${identity.name}`;
}

/**
 * Render a one-line summary of a {@link CardRecord} for the storage card-lookup
 * view (Requirement 8.1): the identity-status badge plus, when the card is
 * `IDENTIFIED`, its authoritative identity. An `UNIDENTIFIED` card renders
 * gracefully from its `cardId` alone — it never assumes an authoritative
 * identity, and the retained ML suggestion (if any) is shown labeled as a
 * suggestion rather than as a verified fact (Requirement 7.4).
 */
function CardIdentitySummary({ card }: { card: CardRecord }): JSX.Element {
  const identified = card.identityStatus === 'IDENTIFIED' && card.identity !== undefined;
  const suggested = card.suggestion?.identification;
  return (
    <span>
      <CardIdentityBadge status={card.identityStatus} />
      {identified && card.identity !== undefined ? (
        <span style={{ marginLeft: 8 }}>{formatCardIdentity(card.identity)}</span>
      ) : suggested !== undefined && suggested.status === 'known' ? (
        <span className="tcvs-muted" style={{ marginLeft: 8 }}>
          Suggested: {formatCardIdentity(suggested.identity)}
        </span>
      ) : null}
      {/* External listing-service SKU (sku-write-back) — shown only when set. */}
      {card.sku !== undefined && card.sku !== '' ? (
        <span className="tcvs-mono" style={{ marginLeft: 8 }}>
          SKU {card.sku}
        </span>
      ) : null}
    </span>
  );
}

function batchNeedsAttention(entry: ContainerListingEntry): boolean {
  const status = entry.batchStatus;
  if (entry.entityType !== 'BATCH' || status === undefined) return false;
  return !(status.reviewComplete ?? status.imageApprovalComplete) || !status.putAway;
}

function sortBatchAttentionFirst(
  entries: readonly ContainerListingEntry[],
): ContainerListingEntry[] {
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const an = batchNeedsAttention(a.entry);
      const bn = batchNeedsAttention(b.entry);
      if (an !== bn) return an ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ entry }) => entry);
}

function ImageApprovalStatusBadge({
  status,
}: {
  status: StorageBatchWorkflowStatus | undefined;
}): JSX.Element {
  if (status === undefined) return <span className="tcvs-muted">Unknown</span>;
  if (status.cardCount === 0) return <span className="tcvs-muted">No cards</span>;
  const { approvedCount, pendingCount, rejectedCount } = status.imageApproval;
  if (pendingCount > 0) {
    return (
      <span>
        <strong className="tcvs-tone-unplaced">Pending</strong>{' '}
        <span className="tcvs-muted">
          {approvedCount}/{status.cardCount} approved
          {rejectedCount > 0 ? `, ${rejectedCount} rejected` : ''}
        </span>
      </span>
    );
  }
  if (rejectedCount > 0) {
    return (
      <span>
        <strong className="tcvs-tone-pulled">Reviewed</strong>{' '}
        <span className="tcvs-muted">
          {approvedCount} approved, {rejectedCount} rejected
        </span>
      </span>
    );
  }
  return (
    <span>
      <strong className="tcvs-tone-stored">Approved</strong>{' '}
      <span className="tcvs-muted">
        {approvedCount}/{status.cardCount}
      </span>
    </span>
  );
}

function PutawayStatusBadge({
  status,
}: {
  status: StorageBatchWorkflowStatus | undefined;
}): JSX.Element {
  if (status === undefined) return <span className="tcvs-muted">Unknown</span>;
  return status.putAway ? (
    <strong className="tcvs-tone-stored">Put away</strong>
  ) : (
    <strong className="tcvs-tone-unplaced">Not put away</strong>
  );
}

function ContainerLocationCell({ entry }: { entry: ContainerListingEntry }): JSX.Element {
  const status = classifyContainerPlacement(entry);
  const parentType = storageEntityTypeFromId(entry.parentId);
  if (status.severity === 'root') return <strong className="tcvs-tone-stored">Root</strong>;
  if (status.severity === 'placed' && entry.parentId !== undefined && parentType !== undefined) {
    return <Link to={entityDetailHref(parentType, entry.parentId)}>{status.label}</Link>;
  }
  return (
    <span className="tcvs-container-location-issue">
      <strong
        className={status.severity === 'critical' ? 'tcvs-tone-pulled' : 'tcvs-tone-unplaced'}
      >
        {status.label}
      </strong>
      <Link
        className="tcvs-linklike"
        to={`/storage?section=Putaway&item=${encodeURIComponent(entry.entityId)}`}
      >
        Putaway now
      </Link>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Create section
// ---------------------------------------------------------------------------

/** One entry in the Create page's "Recently created" list. Carries the human
 *  display label plus the fields {@link labelRequestRef} needs so a label can be
 *  viewed/printed inline without re-finding the entity in Browse. */
interface CreatedEntry {
  label: string;
  entity: ContainerListingEntry;
}

function CreateSection(): JSX.Element {
  const api = useStorageApi();
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedEntry[]>([]);
  // Per-row label view state keyed by entityId, mirroring BrowseSection so one
  // row's label (or its failure) never affects another.
  const [rowLabels, setRowLabels] = useState<Record<string, RowLabelState>>({});

  const note = useCallback(
    (entry: CreatedEntry) => setCreated((prev) => prependCreatedEntry(entry, prev)),
    [],
  );

  const [rackCode, setRackCode] = useState('');
  const onCreateRack = useCallback(async () => {
    setError(null);
    if (rackCode.trim() === '') {
      setError('rack code is required');
      return;
    }
    const result = await api.createRack({ rackCode: rackCode.trim() });
    if (!result.ok) {
      setError(failMessage(result));
      return;
    }
    const { entityId, rackCode: code } = result.data.entity;
    note({
      label: `Rack ${code} -> ${entityId}`,
      entity: { entityId, entityType: 'RACK', rackCode: code },
    });
    setRackCode('');
  }, [api, rackCode, note]);

  const [shelfCode, setShelfCode] = useState('');
  const onCreateShelf = useCallback(async () => {
    setError(null);
    if (shelfCode.trim() === '') {
      setError('shelf code is required');
      return;
    }
    const result = await api.createShelf({ shelfCode: shelfCode.trim() });
    if (!result.ok) {
      setError(failMessage(result));
      return;
    }
    const { entityId, shelfCode: code } = result.data.entity;
    note({
      label: `Shelf ${code} -> ${entityId}`,
      entity: { entityId, entityType: 'SHELF', shelfCode: code },
    });
    setShelfCode('');
  }, [api, shelfCode, note]);

  const onCreateBox = useCallback(async () => {
    setError(null);
    const result = await api.createBox();
    if (!result.ok) {
      setError(failMessage(result));
      return;
    }
    const { entityId } = result.data.entity;
    note({ label: `Box -> ${entityId}`, entity: { entityId, entityType: 'BOX' } });
  }, [api, note]);

  const [rowCode, setRowCode] = useState('');
  const onCreateRow = useCallback(async () => {
    setError(null);
    const result = await api.createRow(rowCode.trim() === '' ? {} : { rowCode: rowCode.trim() });
    if (!result.ok) {
      setError(failMessage(result));
      return;
    }
    const { entityId, rowCode: code } = result.data.entity;
    note({
      label: `${code !== undefined ? `Row ${code}` : 'Row'} -> ${entityId}`,
      entity: { entityId, entityType: 'ROW', ...(code !== undefined ? { rowCode: code } : {}) },
    });
    setRowCode('');
  }, [api, rowCode, note]);

  const [sourceRunId, setSourceRunId] = useState('');
  const onCreateBatch = useCallback(async () => {
    setError(null);
    const result = await api.createBatch(
      sourceRunId.trim() === '' ? {} : { sourceRunId: sourceRunId.trim() },
    );
    if (!result.ok) {
      setError(failMessage(result));
      return;
    }
    const { entityId } = result.data.entity;
    note({ label: `Storage_Batch -> ${entityId}`, entity: { entityId, entityType: 'BATCH' } });
    setSourceRunId('');
  }, [api, sourceRunId, note]);

  // Fetch a created entity's QR_Token and render it inline (mirrors
  // BrowseSection.onViewLabel); a failure is recorded for that row only.
  const onViewLabel = useCallback(
    async (entity: ContainerListingEntry) => {
      const ref = labelRequestRef(entity);
      setRowLabels((prev) => ({ ...prev, [entity.entityId]: { status: 'loading' } }));
      const res = await api.labels(ref.type, ref.id);
      setRowLabels((prev) =>
        res.ok
          ? { ...prev, [entity.entityId]: { status: 'ok', label: res.data } }
          : { ...prev, [entity.entityId]: { status: 'error', message: failMessage(res) } },
      );
    },
    [api],
  );

  const onHideLabel = useCallback((entityId: string) => {
    setRowLabels((prev) => {
      const next = { ...prev };
      delete next[entityId];
      return next;
    });
  }, []);

  return (
    <div>
      <ErrorLine error={error} />
      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Create Kit</h3>
        <p className="tcvs-muted">
          Build a sale kit from selected card fronts and generate stitched listing images.
        </p>
        <Link to="/kitting" className="tcvs-button-link">
          Open Kitting
        </Link>
      </section>

      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Create Rack</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onCreateRack();
          }}
          className="tcvs-form-row"
        >
          <label className="tcvs-field">
            <span>Rack code</span>
            <input
              type="text"
              value={rackCode}
              onChange={(e) => setRackCode(e.target.value)}
              placeholder="Rack N"
            />
          </label>
          <button type="submit">Create Rack</button>
        </form>
        <CreatedLabelEntries
          entries={createdEntriesForType(created, 'RACK')}
          rowLabels={rowLabels}
          onViewLabel={onViewLabel}
          onHideLabel={onHideLabel}
        />
      </section>

      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Create Shelf</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onCreateShelf();
          }}
          className="tcvs-form-row"
        >
          <label className="tcvs-field">
            <span>Shelf code</span>
            <input
              type="text"
              value={shelfCode}
              onChange={(e) => setShelfCode(e.target.value)}
              placeholder="A-01"
            />
          </label>
          <button type="submit">Create Shelf</button>
        </form>
        <CreatedLabelEntries
          entries={createdEntriesForType(created, 'SHELF')}
          rowLabels={rowLabels}
          onViewLabel={onViewLabel}
          onHideLabel={onHideLabel}
        />
      </section>

      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Create Box</h3>
        <button type="button" onClick={() => void onCreateBox()}>
          Create Box
        </button>
        <CreatedLabelEntries
          entries={createdEntriesForType(created, 'BOX')}
          rowLabels={rowLabels}
          onViewLabel={onViewLabel}
          onHideLabel={onHideLabel}
        />
      </section>

      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Create Row</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onCreateRow();
          }}
          className="tcvs-form-row"
        >
          <label className="tcvs-field">
            <span>Row label (optional)</span>
            <input
              type="text"
              value={rowCode}
              onChange={(e) => setRowCode(e.target.value)}
              placeholder="Row 1"
            />
          </label>
          <button type="submit">Create Row</button>
        </form>
        <CreatedLabelEntries
          entries={createdEntriesForType(created, 'ROW')}
          rowLabels={rowLabels}
          onViewLabel={onViewLabel}
          onHideLabel={onHideLabel}
        />
      </section>

      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Create Storage_Batch</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onCreateBatch();
          }}
          className="tcvs-form-row"
        >
          <label className="tcvs-field">
            <span>Source run id (optional)</span>
            <input
              type="text"
              value={sourceRunId}
              onChange={(e) => setSourceRunId(e.target.value)}
              placeholder="leave blank for a manual batch"
              style={{ width: 320 }}
            />
          </label>
          <button type="submit">Create Storage_Batch</button>
        </form>
        <CreatedLabelEntries
          entries={createdEntriesForType(created, 'BATCH')}
          rowLabels={rowLabels}
          onViewLabel={onViewLabel}
          onHideLabel={onHideLabel}
        />
      </section>
    </div>
  );
}

function CreatedLabelEntries({
  entries,
  rowLabels,
  onViewLabel,
  onHideLabel,
}: {
  entries: CreatedEntry[];
  rowLabels: Record<string, RowLabelState>;
  onViewLabel: (entity: ContainerListingEntry) => Promise<void>;
  onHideLabel: (entityId: string) => void;
}): JSX.Element | null {
  if (entries.length === 0) return null;

  return (
    <div style={{ marginTop: 12, minWidth: 0 }}>
      <p className="tcvs-muted" style={{ margin: '0 0 4px' }}>
        Recently created
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {entries.map((entry) => {
          const rowLabel = rowLabels[entry.entity.entityId];
          return (
            <li key={entry.entity.entityId} style={{ padding: '6px 0', minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                  minWidth: 0,
                }}
              >
                <span className="tcvs-mono" style={{ overflowWrap: 'anywhere' }}>
                  {entry.label}
                </span>
                {rowLabel !== undefined ? (
                  <button
                    type="button"
                    className="tcvs-row-action"
                    onClick={() => onHideLabel(entry.entity.entityId)}
                  >
                    Hide label
                  </button>
                ) : (
                  <button
                    type="button"
                    className="tcvs-row-action"
                    onClick={() => void onViewLabel(entry.entity)}
                  >
                    View label
                  </button>
                )}
              </div>
              {rowLabel !== undefined ? (
                <div style={{ marginTop: 8, minWidth: 0 }}>
                  <RowLabel entityId={entry.entity.entityId} state={rowLabel} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Container list sections
// ---------------------------------------------------------------------------

const BROWSE_SECTION_TITLES: Record<ContainerType, string> = {
  racks: 'Racks',
  shelves: 'Shelves',
  boxes: 'Boxes',
  rows: 'Rows',
  batches: 'Batches',
};
const DEFAULT_BATCH_RESULT_LIMIT: BatchResultLimit = 5;

/**
 * Browse already-created containers (Requirement 6). Lists entities of the
 * chosen sidebar type via `listContainers`, accumulating pages with the pure
 * `appendPage` helper. A **Load more** control appears only while a
 * `nextPageToken` remains (Req 6.3); a loading indicator shows while a request
 * is in flight (Req 6.4); a failure shows an error message and a **Retry**
 * control (Req 6.5).
 *
 * The per-row label view (task 7.2) and multi-select printing (task 7.3) are
 * rendered consistently across non-Batch container lists.
 */
function BrowseSection({ type }: { type: ContainerType }): JSX.Element {
  const api = useStorageApi();
  const location = useLocation();
  const navigate = useNavigate();
  const loadSeq = useRef(0);
  const [initialBatchContext] = useState(() =>
    type === 'batches' ? browseReturnContextFrom(location.state) : undefined,
  );
  const initialBatchSnapshot = initialBatchContext?.batchBrowseSnapshot;
  const restoredBatchSnapshot = useRef(initialBatchSnapshot);
  const [entries, setEntries] = useState<ContainerListingEntry[]>(
    () => initialBatchSnapshot?.entries ?? [],
  );
  const [nextToken, setNextToken] = useState<string | undefined>(initialBatchSnapshot?.nextToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  // The page cursor of the request that most recently failed, so Retry can
  // re-issue exactly that request (undefined = the first page).
  const [failedPage, setFailedPage] = useState<string | undefined>(undefined);

  // Per-row label state keyed by entityId so a single row's label view (or its
  // failure) never affects any other row (Req 7.1, 7.3). An entry is absent
  // until its **View label** action runs.
  const [rowLabels, setRowLabels] = useState<Record<string, RowLabelState>>({});

  // Multi-select state (Req 8.1): the set of selected entityIds, the assembled
  // label sheet (resolved tiles + failed ids, Req 8.3/8.5), and a flag while
  // the per-entity label requests are in flight.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [sheet, setSheet] = useState<{ tiles: LabelTileData[]; failed: string[] } | null>(null);
  const [sheetLoading, setSheetLoading] = useState(false);

  // Created-column sort. Default to newest-first for the day-to-day "what did
  // I just make?" workflow. Applied as a client-side view transform over the
  // already-loaded entries, so it never re-queries or disturbs pagination.
  const [sortDir, setSortDir] = useState<SortDirection>(initialBatchSnapshot?.sortDir ?? 'desc');
  const [batchAttentionFirst, setBatchAttentionFirst] = useState(
    initialBatchSnapshot?.attentionFirst ?? type === 'batches',
  );
  const [batchIncompleteOnly, setBatchIncompleteOnly] = useState(
    initialBatchSnapshot?.incompleteOnly ?? false,
  );
  const [batchResultLimit, setBatchResultLimit] = useState<BatchResultLimit>(
    initialBatchSnapshot?.resultLimit ?? DEFAULT_BATCH_RESULT_LIMIT,
  );
  const [query, setQuery] = useState(initialBatchSnapshot?.query ?? '');
  const [placementIssuesOnly, setPlacementIssuesOnly] = useState(false);

  const load = useCallback(
    async (
      forType: ContainerType,
      page: string | undefined,
      incompleteOnly = false,
      resultLimit = DEFAULT_BATCH_RESULT_LIMIT,
    ) => {
      const seq = (loadSeq.current += 1);
      setLoading(true);
      setError(null);
      setStatusError(null);
      const options = forType === 'batches' ? { incompleteOnly, limit: resultLimit } : undefined;
      const res = await api.listContainers(forType, page, options);
      if (seq !== loadSeq.current) return;
      setLoading(false);
      if (!res.ok) {
        setError(failMessage(res));
        setStatusLoading(false);
        setFailedPage(page);
        return;
      }
      setEntries((prev) =>
        page === undefined ? appendPage([], res.data) : appendPage(prev, res.data),
      );
      setNextToken(res.data.nextPageToken);
      if (forType === 'batches') {
        if (incompleteOnly) return;
        setStatusLoading(true);
        const statusRes = await api.listContainers(forType, page, {
          includeStatus: true,
          includePreviews: true,
          limit: resultLimit,
        });
        if (seq !== loadSeq.current) return;
        setStatusLoading(false);
        if (!statusRes.ok) {
          setStatusError(failMessage(statusRes));
          return;
        }
        const pageIds = new Set(res.data.items.map((entry) => entry.entityId));
        const byId = new Map(statusRes.data.items.map((entry) => [entry.entityId, entry]));
        setEntries((prev) =>
          prev.map((entry) => {
            if (!pageIds.has(entry.entityId)) return entry;
            const enriched = byId.get(entry.entityId);
            return enriched === undefined
              ? entry
              : {
                  ...entry,
                  ...(enriched.batchStatus !== undefined
                    ? { batchStatus: enriched.batchStatus }
                    : {}),
                  ...(enriched.cardPreviews !== undefined
                    ? { cardPreviews: enriched.cardPreviews }
                    : {}),
                };
          }),
        );
      }
    },
    [api],
  );

  // Reset and load the first page whenever the selected type changes. Clearing
  // the per-row label views avoids carrying one type's labels onto another.
  useEffect(() => {
    const restored = restoredBatchSnapshot.current;
    if (restored !== undefined) {
      restoredBatchSnapshot.current = undefined;
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
      window.requestAnimationFrame(() => window.scrollTo({ top: restored.scrollY }));
      return;
    }
    setEntries([]);
    setNextToken(undefined);
    setRowLabels({});
    setSelected(new Set());
    setSheet(null);
    setSortDir('desc');
    setBatchAttentionFirst(type === 'batches');
    setQuery('');
    setPlacementIssuesOnly(false);
    setStatusLoading(false);
    setStatusError(null);
    setFailedPage(undefined);
    setError(null);
    void load(type, undefined, type === 'batches' && batchIncompleteOnly, batchResultLimit);
  }, [
    type,
    load,
    batchIncompleteOnly,
    batchResultLimit,
    location.pathname,
    location.search,
    navigate,
  ]);

  const onLoadMore = useCallback(() => {
    if (nextToken === undefined) return;
    void load(type, nextToken, type === 'batches' && batchIncompleteOnly, batchResultLimit);
  }, [load, type, nextToken, batchIncompleteOnly, batchResultLimit]);

  const onRetry = useCallback(() => {
    void load(type, failedPage, type === 'batches' && batchIncompleteOnly, batchResultLimit);
  }, [load, type, failedPage, batchIncompleteOnly, batchResultLimit]);

  const resetBatchBrowse = useCallback(() => {
    loadSeq.current += 1;
    setEntries([]);
    setNextToken(undefined);
    setRowLabels({});
    setSelected(new Set());
    setSheet(null);
    setLoading(false);
    setError(null);
    setStatusLoading(false);
    setStatusError(null);
    setFailedPage(undefined);
    window.scrollTo({ top: 0 });
  }, []);

  // Fetch a single entity's QR_Token from the existing labels route and render
  // it inline (Req 7.1, 7.2). On failure, record an inline error for that
  // entity only — every other row is untouched (Req 7.3).
  const onViewLabel = useCallback(
    async (entry: ContainerListingEntry) => {
      const ref = labelRequestRef(entry);
      setRowLabels((prev) => ({ ...prev, [entry.entityId]: { status: 'loading' } }));
      const res = await api.labels(ref.type, ref.id);
      setRowLabels((prev) =>
        res.ok
          ? { ...prev, [entry.entityId]: { status: 'ok', label: res.data } }
          : { ...prev, [entry.entityId]: { status: 'error', message: failMessage(res) } },
      );
    },
    [api],
  );

  const onHideLabel = useCallback((entityId: string) => {
    setRowLabels((prev) => {
      const next = { ...prev };
      delete next[entityId];
      return next;
    });
  }, []);

  // Flip an entityId's membership in the selection set via the pure toggle
  // helper (Req 8.1). Re-printing later picks up the current selection.
  const onToggleSelect = useCallback((entityId: string) => {
    setSelected((prev) => toggleSelection(prev, entityId));
  }, []);

  // Fetch every selected entity's QR_Token in parallel, partition the results
  // into renderable tiles + failed ids with the pure `buildLabelSheet` helper
  // (Req 8.2, 8.3, 8.5), then print the assembled sheet (Req 8.4). Failures do
  // not block the sheet — resolved tiles still print and failed ids are listed.
  const onPrintSelected = useCallback(async () => {
    const ids = entries.map((entry) => entry.entityId).filter((entityId) => selected.has(entityId));
    if (ids.length === 0) return;
    const byId = new Map(entries.map((entry) => [entry.entityId, entry]));
    setSheetLoading(true);
    setSheet(null);
    const results: LabelResultRef[] = await Promise.all(
      ids.map(async (entityId) => {
        const ref = labelRequestRef(byId.get(entityId) as ContainerListingEntry);
        return { entityId, result: await api.labels(ref.type, ref.id) };
      }),
    );
    const built = buildLabelSheet(results);
    setSheet(built);
    setSheetLoading(false);
    if (built.tiles.length > 0) {
      // Defer to let the sheet render before the synchronous print dialog.
      window.setTimeout(printLabelSheet, 0);
    }
  }, [api, entries, selected]);

  const onCloseSheet = useCallback(() => setSheet(null), []);

  const saveBatchReturnSnapshot = useCallback(() => {
    if (type !== 'batches') return undefined;
    return batchBrowseReturnContext({
      entries,
      ...(nextToken !== undefined ? { nextToken } : {}),
      query,
      sortDir,
      attentionFirst: batchAttentionFirst,
      incompleteOnly: batchIncompleteOnly,
      resultLimit: batchResultLimit,
      scrollY: window.scrollY,
    });
  }, [
    batchAttentionFirst,
    batchIncompleteOnly,
    batchResultLimit,
    entries,
    nextToken,
    query,
    sortDir,
    type,
  ]);

  // Cycle the Created sort: desc (newest first) → asc → desc …
  const onToggleSort = useCallback(() => {
    setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);
  const onToggleBatchAttentionSort = useCallback(() => {
    setBatchAttentionFirst((prev) => !prev);
  }, []);

  // The rows to render: the natural listing order until the operator sorts by
  // Created, then a pure client-side view transform over the loaded entries.
  const filteredEntries = entries.filter(
    (entry) =>
      containerListingEntryMatches(entry, query) &&
      (!placementIssuesOnly || classifyContainerPlacement(entry).issue),
  );
  const dateSortedEntries =
    sortDir === null ? filteredEntries : sortEntriesByDate(filteredEntries, sortDir);
  const visibleEntries =
    type === 'batches' && batchAttentionFirst
      ? sortBatchAttentionFirst(dateSortedEntries)
      : dateSortedEntries;

  const selectedCount = selected.size;
  const title = BROWSE_SECTION_TITLES[type];

  const supportsBulkLabels = supportsBulkLabelPrinting(type);
  const colCount = type === 'shelves' || type === 'batches' ? 6 : 5;

  return (
    <section className="tcvs-card">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 220 }}>
          <h3 style={{ marginTop: 0 }}>{title}</h3>
          <p className="tcvs-caption">
            List already-created {title.toLowerCase()} so you can re-find one after a refresh.
          </p>
          {type === 'batches' ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginTop: 6,
                flexWrap: 'wrap',
              }}
            >
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={batchIncompleteOnly}
                  onChange={(event) => {
                    resetBatchBrowse();
                    setBatchIncompleteOnly(event.target.checked);
                  }}
                />
                Incomplete only
              </label>
              <label
                className="tcvs-field"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: 0 }}
              >
                <span>Results</span>
                <select
                  aria-label="Batch results"
                  value={batchResultLimit}
                  onChange={(event) => {
                    resetBatchBrowse();
                    setBatchResultLimit(Number(event.target.value) as BatchResultLimit);
                  }}
                >
                  {BATCH_RESULT_LIMITS.map((limit) => (
                    <option key={limit} value={limit}>
                      {limit}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}
          {type !== 'racks' ? (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 8,
                fontSize: 13,
              }}
            >
              <input
                type="checkbox"
                checked={placementIssuesOnly}
                onChange={(event) => setPlacementIssuesOnly(event.target.checked)}
              />
              Placement issues only
            </label>
          ) : null}
        </div>
        <label className="tcvs-field" style={{ width: '100%', maxWidth: 360, marginLeft: 'auto' }}>
          <span>Find/Filter</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ID, short ID, status, source run..."
            aria-label={`Find or filter ${title.toLowerCase()}`}
            className="tcvs-mono"
            style={{ width: '100%' }}
          />
        </label>
      </div>

      {error !== null ? (
        <div style={{ marginTop: 8 }}>
          <ErrorLine error={error} />
          <button type="button" onClick={onRetry} disabled={loading}>
            Retry
          </button>
        </div>
      ) : null}

      {entries.length > 0 && visibleEntries.length > 0 ? (
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13, marginTop: 8 }}>
          <thead>
            <tr style={{ background: 'var(--tcvs-th-bg)' }}>
              {supportsBulkLabels ? <th style={cellStyle}>Select</th> : null}
              {type === 'shelves' ? <th style={cellStyle}>Shelf code</th> : null}
              {type === 'batches' ? <th style={cellStyle}>Images</th> : null}
              <th style={cellStyle}>Entity id</th>
              <th style={cellStyle}>Location</th>
              {type === 'batches' ? (
                <th
                  style={{ ...cellStyle, cursor: 'pointer', userSelect: 'none' }}
                  onClick={onToggleBatchAttentionSort}
                  title="Sort batches with unfinished Review or putaway status first"
                  aria-sort={batchAttentionFirst ? 'descending' : 'none'}
                >
                  Status{batchAttentionFirst ? ' ▼' : ' ⇅'}
                </th>
              ) : null}
              <th
                style={{ ...cellStyle, cursor: 'pointer', userSelect: 'none' }}
                onClick={onToggleSort}
                title="Sort by creation date"
                aria-sort={
                  sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'
                }
              >
                Created{sortDir === 'asc' ? ' ▲' : sortDir === 'desc' ? ' ▼' : ' ⇅'}
              </th>
              <th style={cellStyle}>Label</th>
            </tr>
          </thead>
          <tbody>
            {visibleEntries.map((entry) => {
              const rowLabel = rowLabels[entry.entityId];
              return (
                <Fragment key={entry.entityId}>
                  <tr>
                    {supportsBulkLabels ? (
                      <td style={cellStyle}>
                        <input
                          type="checkbox"
                          checked={selected.has(entry.entityId)}
                          onChange={() => onToggleSelect(entry.entityId)}
                          aria-label={`Select ${entry.entityId}`}
                        />
                      </td>
                    ) : null}
                    {type === 'shelves' ? <td style={cellStyle}>{entry.shelfCode ?? ''}</td> : null}
                    {type === 'batches' ? (
                      <td style={cellStyle}>
                        <BatchImageStackPreview
                          batchId={entry.entityId}
                          previews={entry.cardPreviews ?? []}
                          browseContext={saveBatchReturnSnapshot()}
                        />
                      </td>
                    ) : null}
                    <td className="tcvs-mono" style={cellStyle}>
                      <Link
                        to={entityDetailHref(entry.entityType, entry.entityId)}
                        state={entry.entityType === 'BATCH' ? saveBatchReturnSnapshot() : undefined}
                      >
                        {entry.entityId}
                      </Link>
                    </td>
                    <td style={cellStyle}>
                      <ContainerLocationCell entry={entry} />
                    </td>
                    {type === 'batches' ? (
                      <td style={cellStyle}>
                        <div style={{ display: 'grid', gap: 2 }}>
                          <ImageApprovalStatusBadge status={entry.batchStatus} />
                          <PutawayStatusBadge status={entry.batchStatus} />
                        </div>
                      </td>
                    ) : null}
                    <td style={cellStyle}>{formatListingDate(entry.entityId)}</td>
                    <td style={cellStyle}>
                      {rowLabel !== undefined ? (
                        <button
                          type="button"
                          className="tcvs-row-action"
                          onClick={() => onHideLabel(entry.entityId)}
                        >
                          Hide label
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="tcvs-row-action"
                          onClick={() => void onViewLabel(entry)}
                        >
                          View label
                        </button>
                      )}
                    </td>
                  </tr>
                  {rowLabel !== undefined ? (
                    <tr>
                      <td style={cellStyle} colSpan={colCount}>
                        <RowLabel entityId={entry.entityId} state={rowLabel} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {type === 'batches' && statusLoading ? (
        <p className="tcvs-muted" style={{ marginTop: 8 }}>
          Loading batch statuses…
        </p>
      ) : null}

      {type === 'batches' && statusError !== null ? (
        <p className="tcvs-error" style={{ marginTop: 8 }}>
          Batch statuses could not be loaded: {statusError}
        </p>
      ) : null}

      {!loading && error === null && entries.length > 0 && visibleEntries.length === 0 ? (
        <p className="tcvs-muted" style={{ marginTop: 8 }}>
          No loaded {title.toLowerCase()} match the active filters.
        </p>
      ) : null}

      {(query.trim() !== '' || placementIssuesOnly) && nextToken !== undefined ? (
        <p className="tcvs-muted" style={{ marginTop: 8 }}>
          Filter applies to loaded {title.toLowerCase()} only. Load more to search the rest.
        </p>
      ) : null}

      {loading ? (
        <p className="tcvs-muted" style={{ marginTop: 8 }}>
          Loading…
        </p>
      ) : null}

      {!loading && error === null && entries.length === 0 ? (
        <p className="tcvs-muted" style={{ marginTop: 8 }}>
          No {title.toLowerCase()} found.
        </p>
      ) : null}

      {nextToken !== undefined ? (
        <button type="button" onClick={onLoadMore} disabled={loading} style={{ marginTop: 8 }}>
          Load more
        </button>
      ) : null}

      {supportsBulkLabels && entries.length > 0 ? (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => void onPrintSelected()}
            disabled={selectedCount === 0 || sheetLoading}
          >
            Print selected labels{selectedCount > 0 ? ` (${selectedCount})` : ''}
          </button>
          {sheetLoading ? <span className="tcvs-muted">Preparing labels…</span> : null}
        </div>
      ) : null}

      {sheet !== null ? <LabelSheet sheet={sheet} onClose={onCloseSheet} /> : null}
    </section>
  );
}

function BatchImageStackPreview({
  batchId,
  previews,
  browseContext,
}: {
  batchId: string;
  previews: ContainerListingEntry['cardPreviews'];
  browseContext: unknown;
}): JSX.Element {
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [hoverPreviewPosition, setHoverPreviewPosition] = useState<HoverPreviewPosition | null>(
    null,
  );
  const cards = previews ?? [];
  const stackImages = cards
    .map((preview) => preview.thumbnailUrl ?? preview.backThumbnailUrl)
    .filter((url): url is string => url !== undefined)
    .slice(0, 3);
  const galleryImages = cards.flatMap((preview) =>
    [
      preview.thumbnailUrl !== undefined
        ? { key: `${preview.cardId}-front`, url: preview.thumbnailUrl, side: 'Front' }
        : undefined,
      preview.backThumbnailUrl !== undefined
        ? { key: `${preview.cardId}-back`, url: preview.backThumbnailUrl, side: 'Back' }
        : undefined,
    ].filter(
      (image): image is { key: string; url: string; side: 'Front' | 'Back' } => image !== undefined,
    ),
  );
  const hasPreview = cards.length > 0;
  const updateHoverPreviewPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (trigger === null) return;
    const rect = trigger.getBoundingClientRect();
    setHoverPreviewPosition(
      positionBatchHoverPreview(
        { left: rect.left, top: rect.top, bottom: rect.bottom },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, []);

  useEffect(() => {
    if (hoverPreviewPosition === null) return;
    window.addEventListener('resize', updateHoverPreviewPosition);
    window.addEventListener('scroll', updateHoverPreviewPosition, true);
    return () => {
      window.removeEventListener('resize', updateHoverPreviewPosition);
      window.removeEventListener('scroll', updateHoverPreviewPosition, true);
    };
  }, [hoverPreviewPosition, updateHoverPreviewPosition]);

  const openHoverPreview = useCallback(() => {
    if (!hasPreview) return;
    updateHoverPreviewPosition();
  }, [hasPreview, updateHoverPreviewPosition]);
  const closeHoverPreview = useCallback(() => setHoverPreviewPosition(null), []);
  const hoverGallery =
    galleryImages.length > 0 ? (
      galleryImages.map((image) => (
        <img key={image.key} src={image.url} alt="" title={image.side} />
      ))
    ) : (
      <span className="tcvs-review-gallery-empty">Images unavailable</span>
    );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="tcvs-batch-image-stack-trigger"
        aria-label={
          hasPreview
            ? `Open batch ${batchId}; hover to preview card images`
            : `No card images available for ${batchId}`
        }
        onMouseEnter={openHoverPreview}
        onMouseLeave={closeHoverPreview}
        onFocus={openHoverPreview}
        onBlur={closeHoverPreview}
        onClick={() => {
          navigate(entityDetailHref('BATCH', batchId), { state: browseContext });
        }}
        disabled={!hasPreview}
      >
        <span className="tcvs-batch-image-stack" aria-hidden="true">
          {stackImages.length > 0 ? (
            stackImages.map((url, index) => (
              <img
                key={url}
                src={url}
                alt=""
                loading="lazy"
                style={{ '--stack-index': index } as React.CSSProperties}
              />
            ))
          ) : (
            <span className="tcvs-batch-image-stack-empty">{hasPreview ? 'Images' : '—'}</span>
          )}
        </span>
      </button>
      {hoverPreviewPosition !== null && typeof document !== 'undefined'
        ? createPortal(
            <span
              className="tcvs-review-hover-gallery tcvs-batch-image-hover-gallery-portal"
              style={{ left: hoverPreviewPosition.left, top: hoverPreviewPosition.top }}
              aria-hidden="true"
            >
              {hoverGallery}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

/** Per-row label view state keyed by entityId in {@link BrowseSection}. */
type RowLabelState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; label: LabelResponse };

/**
 * Render a single listed entity's QR label inline (Req 7.4–7.6), matching the
 * `.tcvs-print-label` styling established by {@link LabelsSection}: a dashed
 * border, the QR code, the token text, and a **Print** button. The entityId is
 * shown alongside the code (Req 7.5). A fetch failure renders an inline error
 * for this entity only (Req 7.3).
 */
function RowLabel({ entityId, state }: { entityId: string; state: RowLabelState }): JSX.Element {
  if (state.status === 'loading') {
    return (
      <p className="tcvs-muted" style={{ margin: 0 }}>
        Loading label…
      </p>
    );
  }
  if (state.status === 'error') {
    return <ErrorLine error={state.message} />;
  }
  const { label } = state;
  return (
    <LabelTile
      token={label.token}
      entityId={entityId}
      qrMaxWidth={65}
      containerStyle={{
        border: '2px dashed #333',
        padding: 12,
        display: 'inline-grid',
        textAlign: 'center',
      }}
    >
      <button type="button" onClick={printSingleLabel}>
        Print
      </button>
    </LabelTile>
  );
}

/**
 * Render a Label_Sheet for a multi-entity selection (Req 8.3–8.5). Resolved
 * tiles render one `qrcode.react` QR per entity, each labeled with its
 * entityId. Entities whose QR_Token could not be obtained are listed so the
 * Operator sees exactly which selections were not labeled (Req 8.5); the sheet
 * still renders for the entities that resolved.
 *
 * The sheet container carries `.tcvs-print-sheet` (visible only in print, see
 * the `@media print` block in `index.html`) wrapping `.tcvs-print-label` tiles
 * that flow down the page, so only the labels reach the printer (Req 8.1–8.4).
 */
function LabelSheet({
  sheet,
  onClose,
}: {
  sheet: { tiles: LabelTileData[]; failed: string[] };
  onClose: () => void;
}): JSX.Element {
  return (
    <div style={{ marginTop: 16 }}>
      <div
        className="tcvs-print-controls"
        style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}
      >
        <strong>
          Label sheet — {sheet.tiles.length} label{sheet.tiles.length === 1 ? '' : 's'}
        </strong>
        <button type="button" onClick={printLabelSheet} disabled={sheet.tiles.length === 0}>
          Print sheet
        </button>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>

      {sheet.failed.length > 0 ? (
        <div className="tcvs-print-controls" style={{ marginBottom: 8 }}>
          <p className="tcvs-error" style={{ margin: 0 }}>
            Could not label {sheet.failed.length} selected{' '}
            {sheet.failed.length === 1 ? 'container' : 'containers'}:
          </p>
          <ul className="tcvs-mono tcvs-error" style={{ margin: '4px 0 0' }}>
            {sheet.failed.map((entityId) => (
              <li key={entityId}>{entityId}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="tcvs-print-sheet" style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {sheet.tiles.map((tile) => (
          <LabelTile
            key={tile.entityId}
            token={tile.token}
            entityId={tile.entityId}
            qrMaxWidth={65}
            containerStyle={{
              border: '2px dashed #333',
              padding: 12,
              display: 'inline-grid',
              textAlign: 'center',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card label printing (co-located with Pick and Find)
//
// A card carries no physical label by default — it is tracked through its
// containing Storage_Batch's label. An INDIVIDUAL card label is needed only
// when a card is (or is about to be) separated from its batch: pulled for
// processing-then-restock, pulled to ship, or a loose card found out of place.
// So card-label printing lives where those moments happen — next to Pick and in
// Find — rather than as a standalone "Labels" destination. The backend labels
// route resolves a short code or a cardId to the canonical `tcvs:card:<cardId>`
// token, so this works from whatever identifier the operator has in hand.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Pick section
// ---------------------------------------------------------------------------

function PickSection(): JSX.Element {
  const api = useStorageApi();
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [lastPick, setLastPick] = useState<PickResponse | null>(null);
  const [restocking, setRestocking] = useState(false);

  const [pickCardId, setPickCardId] = useState('');
  const onPick = useCallback(async () => {
    setError(null);
    setResult(null);
    setLastPick(null);
    const cardRef = pickCardId.trim();
    if (cardRef === '') {
      setError('Enter a card id, short code, or SKU.');
      return;
    }
    const confirmed = window.confirm(
      `Pick ${cardRef}? This will mark the card as pulled from storage.`,
    );
    if (!confirmed) return;
    const res = await api.pick(cardRef);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    setLastPick(res.data);
    setResult(`Pick ok: card ${res.data.cardId} pulled`);
  }, [api, pickCardId]);

  const onPutBack = useCallback(async () => {
    if (lastPick?.sourceBatchId === undefined) return;
    setError(null);
    setRestocking(true);
    const res = await api.putaway(
      { type: 'CARD', cardId: lastPick.cardId },
      lastPick.sourceBatchId,
    );
    setRestocking(false);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    setResult(`Put back ok: card ${lastPick.cardId} returned to ${lastPick.sourceBatchId}`);
    setLastPick(null);
  }, [api, lastPick]);

  return (
    <div>
      <FulfillmentPickWorkbench />
      <ErrorLine error={error} />
      {result !== null ? <p className="tcvs-success-text">{result}</p> : null}
      {lastPick?.sourceBatchId !== undefined ? (
        <section className="tcvs-card tcvs-card-success">
          <h3 className="tcvs-heading-success" style={{ marginTop: 0 }}>
            Card pulled
          </h3>
          <p style={{ marginTop: 0 }}>
            Source batch: <span className="tcvs-mono">{lastPick.sourceBatchId}</span>
          </p>
          <button
            type="button"
            className="tcvs-btn-secondary"
            disabled={restocking}
            onClick={() => void onPutBack()}
          >
            {restocking ? 'Putting back…' : 'Put back in same batch'}
          </button>
        </section>
      ) : null}

      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Manual pull</h3>
        <p className="tcvs-caption">
          Recovery tool for a card that is not part of marketplace fulfillment work.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onPick();
          }}
          className="tcvs-form-row"
        >
          <label className="tcvs-field">
            <span>Card id, short code, or SKU</span>
            <input
              type="text"
              value={pickCardId}
              onChange={(e) => setPickCardId(e.target.value)}
              style={{ width: 280 }}
            />
          </label>
          <button type="submit">Pick</button>
          {/* Independent of the pick: print the card's label so a pulled card
              stays identifiable away from its batch (e.g. for restock or
              shipping). Prints for whatever id/short code is in the field. */}
          <CardLabelButton getCardId={() => pickCardId} />
        </form>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Putaway wizard
// ---------------------------------------------------------------------------

function ScanPutawaySection(): JSX.Element {
  const api = useStorageApi();
  const [params] = useSearchParams();
  const [subjectToken, setSubjectToken] = useState(() => params.get('item') ?? '');
  const [targetToken, setTargetToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<PutawayResponse | null>(null);
  const [readyToCommit, setReadyToCommit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resolving, setResolving] = useState<'subject' | 'target' | null>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const targetInputRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  // Which token field, if any, is currently being scanned with the camera.
  const [scanning, setScanning] = useState<'subject' | 'target' | null>(null);

  const startScan = useCallback((target: 'subject' | 'target') => {
    setError(null);
    setOutcome(null);
    setReadyToCommit(false);
    setScanning(target);
  }, []);

  // Camera, paste, keyboard-wedge, and manual input all reach the same server
  // normalization on explicit submit. Preserve the scanned value for correction.
  const onScanResult = useCallback((text: string) => {
    setReadyToCommit(false);
    setScanning((current) => {
      if (current === 'subject') setSubjectToken(text);
      else if (current === 'target') setTargetToken(text);
      return null;
    });
  }, []);

  const submit = useCallback(
    async (subjectRef = subjectToken.trim(), targetRef = targetToken.trim()) => {
      setError(null);
      setOutcome(null);
      setReadyToCommit(false);
      if (subjectRef === '' || targetRef === '') {
        setError('Scan or enter both the item and destination.');
        return;
      }
      setSubmitting(true);
      try {
        const res = await api.putawayByReference(subjectRef, targetRef);
        if (!res.ok) {
          setError(putawayFailureMessage(res));
          return;
        }
        setOutcome(res.data);
        setSubjectToken('');
        setTargetToken('');
        subjectInputRef.current?.focus();
      } catch {
        setError(
          'The response could not be confirmed. Retry safely; if the first request succeeded, TCVS will report Already there.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [api, subjectToken, targetToken],
  );

  const resolveOnEnter = useCallback(
    async (field: 'subject' | 'target') => {
      const raw = field === 'subject' ? subjectToken : targetToken;
      const reference = raw.trim();
      setError(null);
      setOutcome(null);
      if (reference === '') {
        setError(`Scan or enter the ${field === 'subject' ? 'item' : 'destination'} first.`);
        return;
      }

      setResolving(field);
      try {
        const result = await api.resolvePutawayReference(reference);
        if (!result.ok) {
          setError(putawayFailureMessage(result));
          return;
        }

        if (field === 'subject') {
          targetInputRef.current?.focus();
        } else {
          setReadyToCommit(true);
          submitButtonRef.current?.focus();
        }
      } catch {
        setError('The container could not be checked. Verify the connection and retry.');
      } finally {
        setResolving(null);
      }
    },
    [api, subjectToken, targetToken],
  );

  return (
    <div>
      {scanning !== null ? (
        <Suspense fallback={null}>
          <QrScanner
            title={scanning === 'subject' ? 'Item QR label' : 'Destination QR label'}
            onResult={onScanResult}
            onClose={() => setScanning(null)}
          />
        </Suspense>
      ) : null}
      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Putaway</h3>
        <p className="tcvs-caption">
          Place or relocate inventory by scanning or entering the item, then its destination. The
          backend records putaway, move, or already-there based on current location.
        </p>
        <form
          onSubmit={(e) => {
            // Each input handles a wedge scanner's Enter explicitly: item Enter
            // advances after validation; destination Enter validates and focuses
            // the explicit commit control without mutating placement.
            e.preventDefault();
          }}
          style={{ display: 'grid', gap: 8, maxWidth: 520 }}
        >
          <label className="tcvs-field">
            <span>1. Item label or short ID</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={subjectToken}
                onChange={(e) => {
                  setSubjectToken(e.target.value);
                  setReadyToCommit(false);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  void resolveOnEnter('subject');
                }}
                ref={subjectInputRef}
                placeholder="tcvs:batch:01HQ... or 01HQ..."
                className="tcvs-mono"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="tcvs-btn-secondary"
                onClick={() => startScan('subject')}
              >
                📷 Scan
              </button>
            </div>
          </label>
          <label className="tcvs-field">
            <span>2. Destination label or short ID</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={targetToken}
                onChange={(e) => {
                  setTargetToken(e.target.value);
                  setReadyToCommit(false);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  void resolveOnEnter('target');
                }}
                ref={targetInputRef}
                placeholder="tcvs:row:01HQ... or 01HQ..."
                className="tcvs-mono"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="tcvs-btn-secondary"
                onClick={() => startScan('target')}
              >
                📷 Scan
              </button>
            </div>
          </label>
          <div>
            <button
              type="button"
              ref={submitButtonRef}
              disabled={submitting || resolving !== null}
              onClick={() => void submit()}
            >
              {submitting ? 'Submitting…' : 'Run putaway'}
            </button>
          </div>
          {readyToCommit ? (
            <p className="tcvs-caption" role="status" style={{ margin: 0 }}>
              Destination checked. Select Run putaway to commit this placement.
            </p>
          ) : null}
        </form>
      </section>

      <ErrorLine error={error} />

      {outcome !== null ? (
        <section className="tcvs-card tcvs-card-success">
          <h3 className="tcvs-heading-success" style={{ marginTop: 0 }}>
            {outcome.status === 'no-op' ? 'Already there' : 'Putaway committed'}
          </h3>
          <p>
            {outcome.subject.entityType} to {outcome.target.entityType}:{' '}
            {outcome.status === 'no-op'
              ? 'already there'
              : outcome.operation === 'MOVE'
                ? 'moved'
                : 'put away'}
            .
          </p>
          <p className="tcvs-caption">
            Confirmed by the server: item{' '}
            <Link
              to={entityDetailHref(outcome.subject.entityType, outcome.subject.entityId)}
              className="tcvs-mono"
            >
              {outcome.subject.entityId}
            </Link>{' '}
            → destination{' '}
            <Link
              to={entityDetailHref(outcome.target.entityType, outcome.target.entityId)}
              className="tcvs-mono"
            >
              {outcome.target.entityId}
            </Link>
            .
          </p>
          <p className="tcvs-caption">
            Item type: <strong>{outcome.subject.entityType}</strong>. Expected destination type:{' '}
            <strong>{outcome.expectedTargetType}</strong>.
          </p>
        </section>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Find section (unified lookup)
//
// One lookup that resolves ANY subject an operator can present and reports its
// current location: a scanned QR token (card OR Shelf/Box/Storage_Batch), a
// typed short code, or a literal cardId. All three go through the single
// `POST /storage/scan` endpoint, which the API unifies server-side — a value
// that is not a QR token falls through to short-code/cardId resolution. This
// replaces the former separate "Card location" and "Scan / resolve a token"
// sections (cards were resolvable in both; only scan could resolve containers).
// ---------------------------------------------------------------------------

function FindSection(): JSX.Element {
  const api = useStorageApi();
  const catalog = useCatalogApi();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [scan, setScan] = useState<ScanResponse | null>(null);
  // On a card hit, the card record is fetched for the identity summary. The
  // location/placement still renders even when this best-effort lookup fails.
  const [card, setCard] = useState<CardRecord | undefined>(undefined);
  const [scanning, setScanning] = useState(false);

  const resolve = useCallback(
    async (raw: string) => {
      const input = raw.trim();
      if (input === '') {
        setError('Enter or scan a card, short code, or container token.');
        return;
      }
      setError(null);
      setScan(null);
      setCard(undefined);
      const res = await api.scan(input);
      if (!res.ok) {
        setError(failMessage(res));
        return;
      }
      setScan(res.data);
      // For a card result, pull the record so the identity (or suggestion) shows
      // alongside the placement — works for UNIDENTIFIED cards too (Req 8.1).
      if (res.data.kind === 'card') {
        const cardRes = await catalog.get(res.data.cardId);
        if (cardRes.ok) setCard(cardRes.data);
      }
    },
    [api, catalog],
  );

  // A scanned QR token resolves immediately — no separate submit press.
  const onScanResult = useCallback(
    (text: string) => {
      setScanning(false);
      setValue(text.trim());
      void resolve(text);
    },
    [resolve],
  );

  return (
    <>
      {scanning ? (
        <Suspense fallback={null}>
          <QrScanner
            title="Scan a card or container label"
            onResult={onScanResult}
            onClose={() => setScanning(false)}
          />
        </Suspense>
      ) : null}
      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Find a card or container</h3>
        <p className="tcvs-caption">
          Scan a QR label, or type a card ID, short code, SKU, container token, or bare container
          ID. Shows what it is and where it currently lives. A SKU shared by more than one card
          lists them all.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void resolve(value);
          }}
          className="tcvs-form-row"
        >
          <label className="tcvs-field">
            <span>Card ID, short code, SKU, or token</span>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. K7M4QX  ·  3538  ·  01HQ…  ·  tcvs:box:01HQ…"
              className="tcvs-mono"
              style={{ width: 420 }}
              autoFocus
            />
          </label>
          <button type="submit">Look up</button>
          <button type="button" className="tcvs-btn-secondary" onClick={() => setScanning(true)}>
            📷 Scan
          </button>
        </form>
        <ErrorLine error={error} />
        {scan !== null ? (
          <div style={{ marginTop: 8 }}>
            {scan.kind === 'card' ? (
              <>
                {card !== undefined ? (
                  <p style={{ margin: '0 0 4px' }}>
                    <CardIdentitySummary card={card} />
                  </p>
                ) : null}
                <p style={{ margin: 0 }}>
                  Card <code className="tcvs-mono">{scan.cardId}</code> —{' '}
                  <PlacementBadge display={describePlacement(scan.location)} />
                </p>
                <p style={{ margin: '4px 0 0' }}>
                  <Link to={`/catalog/${scan.cardId}`}>View card details</Link>
                </p>
                {/* Print this card's label — useful when you've located a loose
                    card and need it identifiable to restock it. */}
                <div style={{ marginTop: 8 }}>
                  <CardLabelButton getCardId={() => (scan.kind === 'card' ? scan.cardId : '')} />
                </div>
              </>
            ) : scan.kind === 'cards' ? (
              <>
                <p style={{ margin: '0 0 4px' }}>
                  {scan.cards.length} cards share SKU <code className="tcvs-mono">{scan.sku}</code>:
                </p>
                <ul style={{ margin: 0 }}>
                  {scan.cards.map((c) => (
                    <li key={c.cardId}>
                      Card <code className="tcvs-mono">{c.cardId}</code>
                      {c.shortCode !== undefined ? (
                        <>
                          {' '}
                          (<code className="tcvs-mono">{c.shortCode}</code>)
                        </>
                      ) : null}{' '}
                      — <PlacementBadge display={describePlacement(c.location)} />{' '}
                      <Link to={`/catalog/${c.cardId}`}>details</Link>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p style={{ margin: 0 }}>
                {scan.entity.entityType}{' '}
                <Link
                  to={entityDetailHref(scan.entity.entityType, scan.entity.entityId)}
                  className="tcvs-mono"
                >
                  {scan.entity.entityId}
                </Link>{' '}
                —{' '}
                {scan.location.status === 'PLACED'
                  ? formatEntityScanPath(scan.location.path)
                  : 'not yet placed'}
              </p>
            )}
          </div>
        ) : null}
      </section>
      <section className="tcvs-card">
        <CardSearchPanel
          title="Search cards"
          intro="Filter card records by category, set, or name."
          showSkuFilter={false}
        />
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Picklist section
// ---------------------------------------------------------------------------

function PicklistSection(): JSX.Element {
  const api = useStorageApi();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<PicklistEntry[] | null>(null);

  const onSubmit = useCallback(async () => {
    setError(null);
    setEntries(null);
    const cardIds = text
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => s !== '');
    if (cardIds.length === 0) {
      setError('paste at least one card id');
      return;
    }
    const res = await api.picklist(cardIds);
    if (!res.ok) {
      setError(failMessage(res));
      return;
    }
    setEntries(res.data.entries);
  }, [api, text]);

  const grouped = entries === null ? null : groupPicklist(entries);
  // A best-effort cardId → preview lookup built from the raw entries (each entry
  // carries an optional presigned `thumbnailUrl` + `shortCode`). The grouping
  // helper keeps only cardIds for ordering, so previews are looked up at render.
  const previewByCardId = new Map<string, { thumbnailUrl?: string; shortCode?: string }>();
  for (const e of entries ?? []) {
    if (e.thumbnailUrl !== undefined || e.shortCode !== undefined) {
      previewByCardId.set(e.cardId, {
        ...(e.thumbnailUrl !== undefined ? { thumbnailUrl: e.thumbnailUrl } : {}),
        ...(e.shortCode !== undefined ? { shortCode: e.shortCode } : {}),
      });
    }
  }

  return (
    <div>
      <section className="tcvs-card">
        <h3 style={{ marginTop: 0 }}>Picklist</h3>
        <p className="tcvs-caption">
          Paste card ids, short codes, or SKUs (whitespace- or comma-separated). A SKU that matches
          several cards lists them all. Results are grouped and sorted for physical retrieval: Shelf
          → Box → Batch.
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          className="tcvs-mono"
          style={{ width: '100%' }}
          placeholder="card-id-1, card-id-2, ..."
        />
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => void onSubmit()}>
            Resolve picklist
          </button>
        </div>
      </section>

      <ErrorLine error={error} />

      {grouped !== null ? (
        <section className="tcvs-card">
          <h3 style={{ marginTop: 0 }}>Retrieval order</h3>
          {grouped.stored.length === 0 ? (
            <p className="tcvs-muted">No fully-located (STORED) cards.</p>
          ) : (
            grouped.stored.map((shelfGroup) => (
              <div key={shelfGroup.shelf.entityId} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600 }}>
                  Shelf {shelfGroup.shelf.shelfCode}{' '}
                  <code className="tcvs-mono tcvs-muted">{shelfGroup.shelf.entityId}</code>
                </div>
                {shelfGroup.boxes.map((boxGroup) => (
                  <div key={boxGroup.box.entityId} style={{ marginLeft: 16 }}>
                    <div>
                      Box <code className="tcvs-mono">{boxGroup.box.entityId}</code>
                    </div>
                    {boxGroup.batches.map((batchGroup) => (
                      <div key={batchGroup.batch.entityId} style={{ marginLeft: 16 }}>
                        <div>
                          Batch <code className="tcvs-mono">{batchGroup.batch.entityId}</code> —{' '}
                          {batchGroup.cardIds.length} card(s)
                        </div>
                        <div style={{ marginTop: 2 }}>
                          <PicklistCardList
                            cardIds={batchGroup.cardIds}
                            previews={previewByCardId}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))
          )}

          <PicklistBucket
            title="Stored — batch not yet put away"
            rows={grouped.storedUnplaced}
            previews={previewByCardId}
          />
          <PicklistIdBucket
            title="Pulled"
            ids={grouped.pulled}
            previews={previewByCardId}
            linkable
          />
          <PicklistIdBucket
            title="Not yet stored"
            ids={grouped.notPlaced}
            previews={previewByCardId}
            linkable
          />
          <PicklistIdBucket title="Unknown card ids" ids={grouped.unknown} />
        </section>
      ) : null}
    </div>
  );
}

/** One card in a picklist bucket, styled to match the entity/location detail
 *  page's Contents rows: a 72×100 thumbnail (or 🂠 placeholder) + the short code
 *  (falling back to the cardId). Real cards link to their catalog detail; the
 *  Unknown bucket passes `linkable={false}` since its "id" is a raw SKU/short
 *  code with no card record. */
function PicklistCardRow({
  cardId,
  preview,
  linkable = true,
}: {
  cardId: string;
  preview?: { thumbnailUrl?: string; shortCode?: string };
  linkable?: boolean;
}): JSX.Element {
  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    textDecoration: 'none',
  } as const;
  const inner = (
    <>
      {preview?.thumbnailUrl !== undefined ? (
        <img
          src={preview.thumbnailUrl}
          alt=""
          loading="lazy"
          width={72}
          height={100}
          style={{
            objectFit: 'cover',
            borderRadius: 3,
            border: '1px solid var(--tcvs-border)',
            flex: '0 0 auto',
          }}
        />
      ) : (
        <span
          aria-hidden="true"
          style={{
            width: 72,
            height: 100,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 3,
            border: '1px solid var(--tcvs-border)',
            background: 'var(--tcvs-th-bg)',
            fontSize: 32,
            flex: '0 0 auto',
          }}
        >
          🂠
        </span>
      )}
      <span className="tcvs-mono">{preview?.shortCode ?? cardId}</span>
    </>
  );
  return linkable ? (
    <Link to={`/catalog/${cardId}`} style={rowStyle}>
      {inner}
    </Link>
  ) : (
    <span style={rowStyle}>{inner}</span>
  );
}

/** A vertical list of picklist card rows, matching the entity detail Contents
 *  list styling (border-separated rows). Duplicate cardIds are preserved. */
function PicklistCardList({
  cardIds,
  previews,
  linkable = true,
}: {
  cardIds: string[];
  previews?: Map<string, { thumbnailUrl?: string; shortCode?: string }>;
  linkable?: boolean;
}): JSX.Element {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {cardIds.map((id, i) => (
        <li
          key={`${id}-${i}`}
          style={{
            padding: '4px 0',
            borderTop: i > 0 ? '1px solid var(--tcvs-border)' : undefined,
          }}
        >
          <PicklistCardRow cardId={id} preview={previews?.get(id)} linkable={linkable} />
        </li>
      ))}
    </ul>
  );
}

function PicklistBucket({
  title,
  rows,
  previews,
}: {
  title: string;
  rows: { batch: { entityId: string }; cardIds: string[] }[];
  previews: Map<string, { thumbnailUrl?: string; shortCode?: string }>;
}): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontWeight: 600 }}>{title}</div>
      {rows.map((r) => (
        <div key={r.batch.entityId} style={{ marginLeft: 16, marginTop: 4 }}>
          <div>
            Batch <code className="tcvs-mono">{r.batch.entityId}</code>
          </div>
          <PicklistCardList cardIds={r.cardIds} previews={previews} />
        </div>
      ))}
    </div>
  );
}

function PicklistIdBucket({
  title,
  ids,
  previews,
  linkable = false,
}: {
  title: string;
  ids: string[];
  previews?: Map<string, { thumbnailUrl?: string; shortCode?: string }>;
  linkable?: boolean;
}): JSX.Element | null {
  if (ids.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontWeight: 600 }}>
        {title} ({ids.length})
      </div>
      <div style={{ marginLeft: 16 }}>
        <PicklistCardList cardIds={ids} previews={previews} linkable={linkable} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transactions history
// ---------------------------------------------------------------------------

function TransactionsSection(): JSX.Element {
  const api = useStorageApi();
  const [type, setType] = useState<LabelType>('boxes');
  const [id, setId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApiResult<
    import('./storage-api').TransactionsResponse
  > | null>(null);
  const [page, setPage] = useState(1);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);

  const load = useCallback(
    async (token: string | undefined, pageNum: number) => {
      setError(null);
      const res = await api.transactions(type, id.trim(), token);
      if (!res.ok) {
        setError(failMessage(res));
        setResult(null);
        return;
      }
      setResult(res);
      setNextToken(res.data.nextPageToken);
      setPage(pageNum);
    },
    [api, type, id],
  );

  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>Transaction history</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void load(undefined, 1);
        }}
        className="tcvs-form-row"
      >
        <label className="tcvs-field">
          <span>Type</span>
          <select value={type} onChange={(e) => setType(e.target.value as LabelType)}>
            <option value="shelves">Shelf</option>
            <option value="boxes">Box</option>
            <option value="batches">Storage_Batch</option>
            <option value="cards">Card</option>
          </select>
        </label>
        <label className="tcvs-field">
          <span>Id</span>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value)}
            className="tcvs-mono"
            style={{ width: 360 }}
          />
        </label>
        <button type="submit">Load history</button>
      </form>
      <ErrorLine error={error} />
      {result !== null && result.ok ? (
        <div style={{ marginTop: 8 }}>
          <p className="tcvs-muted">Page {page} (newest first).</p>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--tcvs-th-bg)' }}>
                <th style={cellStyle}>When</th>
                <th style={cellStyle}>Op</th>
                <th style={cellStyle}>Actor</th>
                <th style={cellStyle}>From → To</th>
              </tr>
            </thead>
            <tbody>
              {result.data.items.map((t) => (
                <tr key={t.txnId}>
                  <td style={cellStyle}>{t.timestamp}</td>
                  <td style={cellStyle}>{t.opType}</td>
                  <td className="tcvs-mono" style={cellStyle}>
                    {t.actorId}
                  </td>
                  <td className="tcvs-mono" style={cellStyle}>
                    {formatTxnTransition(t)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {nextToken !== undefined ? (
            <button
              type="button"
              onClick={() => void load(nextToken, page + 1)}
              style={{ marginTop: 8 }}
            >
              Next page
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** Render the source→destination (or state transition) of a transaction. */
function formatTxnTransition(t: import('../shared/types').StorageTransaction): string {
  if (t.fromState !== undefined || t.toState !== undefined) {
    return `${t.fromState ?? '∅'} → ${t.toState ?? '∅'}`;
  }
  return `${t.fromParentId ?? '∅'} → ${t.toParentId ?? '∅'}`;
}
